import React from 'react';
import { MPFButton } from 'volto-ministerio-publico-federal/components/UI';
import { type OndeAtuaTema } from './helpers';

/**
 * Card claro do tema: subtítulo, texto e o botão da página do tema.
 *
 * Dois contextos, mesmo componente (por isso mora em arquivo próprio — os
 * ramos que o renderizam moram em `OndeAtuaSection`):
 *   - `lg`+ (desktop): coluna da esquerda, ao lado do carrossel;
 *   - abaixo de `lg` (celular E tablet): primeiro slide do carrossel único
 *     (`OndeAtuaCarouselMobile`). No celular o slide é a largura toda; no
 *     tablet, uma fração dela (`slidesPerView` ~2.3, com espiada do próximo
 *     card) — mas para este card o efeito é só a largura do slide mudar, a
 *     estrutura interna é a mesma nos dois.
 */
export const OndeAtuaTemaCard: React.FC<{ tema: OndeAtuaTema }> = ({
  tema,
}) => (
  /* Sem traço próprio: quem fecha a faixa é a borda da `<section>`, e um
     segundo contorno aqui dentro viraria caixa dentro de caixa. O card se
     separa do fundo pelo degradê de teal, que mora no SCSS
     (`mpf-onde-atua__tema`) porque precisa de duas camadas de fundo — daí
     também a ausência de `bg-*`: as utilities do Tailwind entram depois do
     nosso CSS e sobrescreveriam o degradê. */
  <div className="mpf-onde-atua__tema flex h-full flex-col rounded-2xl p-4 dark:bg-night-800">
    {tema.subtitulo && (
      /* 14/22: é um olho acima do texto, não um título — fica menor que o
         corpo de propósito, e o cinza-500 completa a hierarquia. */
      <p className="text-sm font-normal text-gray-500 dark:text-night-400">
        {tema.subtitulo}
      </p>
    )}
    {tema.texto && (
      /* 16/24 no cinza de leitura, como no mockup. */
      <p className="mt-3 text-base font-normal text-on-surface dark:text-night-100">
        {tema.texto}
      </p>
    )}

    {tema.ctaLabel && tema.ctaHref && (
      /* No desktop (`lg`+) o card estica junto com a coluna do carrossel, e é
         o `lg:mt-auto` que cola o botão no rodapé (`lg:pt-6` é o respiro
         mínimo quando o texto é longo o bastante para não sobrar espaço).
         Abaixo de `lg` (celular E tablet) o card também é `h-full`, mas
         dentro do carrossel único ele estica para a altura do slide vizinho
         (a foto) — que é sempre mais alto. Um `mt-auto` ali empurraria o
         botão para o rodapé desse slide mais alto, abrindo um vão enorme
         entre o texto e o botão. Por isso, abaixo de `lg`, o espaçamento é
         fixo — 16px, como no Figma: o vão sobra abaixo do botão, dentro do
         degradê, e não mais entre o parágrafo e ele. O corte é `lg`, e não
         `md`: o card continua sendo SLIDE (não coluna de grade) até `lg`,
         mesmo no tablet — ver `OndeAtuaSection`. */
      <div className="mt-4 lg:mt-auto lg:pt-6">
        <MPFButton
          as="a"
          href={tema.ctaHref}
          /* Largura inteira nas duas telas, como o Figma pede: o botão vai de
             padding a padding do card. Isto só é seguro depois da correção de
             `box-sizing` em `_capa-onde-atua.scss` — sem ela, o `w-full` do
             `--block` somava o próprio padding e transbordava o slide. */
          fullWidth
          size="l"
          {...(tema.ctaExternal
            ? { target: '_blank', rel: 'noopener noreferrer' }
            : {})}
        >
          {tema.ctaLabel}
          {tema.ctaExternal && (
            <span className="sr-only"> (abre em nova aba)</span>
          )}
        </MPFButton>
      </div>
    )}
  </div>
);

export default OndeAtuaTemaCard;
